import {
  asNumber,
  cleanText,
  formatPlainMoney,
} from "./dtePrintFormatters";
import {
  getDteTemplateLabel,
  isDteTemplateFieldVisible,
} from "@/helpers/dteTemplateSettings";

export function getStandardThemeColors(documentData = {}) {
  const theme = documentData.theme || {};

  return {
    bg: theme.brand || "#808080",
    fg: theme.brandContrast || "#ffffff",
  };
}

export function getStandardCurrencyLabel(currency) {
  if (currency === "$") {
    return "USD";
  }

  return cleanText(currency, "---");
}

export function getStandardShowInternalCorrelative() {
  try {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    return Boolean(user.showcorrelativecontrol);
  } catch (error) {
    return false;
  }
}

export function getStandardRecipientRows(documentData = {}) {
  const receiver = documentData.receiver || {};
  const templateSettings = documentData.templateSettings || {};
  const rows = [];
  const showCustomerCode = isDteTemplateFieldVisible(
    templateSettings,
    "receiver",
    "customerCode"
  );
  const showSellerCode = isDteTemplateFieldVisible(
    templateSettings,
    "receiver",
    "sellerCode"
  );
  const showSellerName = isDteTemplateFieldVisible(
    templateSettings,
    "receiver",
    "sellerName"
  );
  const showContact = isDteTemplateFieldVisible(
    templateSettings,
    "receiver",
    "contact"
  );
  const showEntity = isDteTemplateFieldVisible(
    templateSettings,
    "receiver",
    "entity"
  );
  const showEconomicActivity = isDteTemplateFieldVisible(
    templateSettings,
    "receiver",
    "economicActivity"
  );
  const showPaymentStatus = isDteTemplateFieldVisible(
    templateSettings,
    "receiver",
    "paymentStatus"
  );
  const showZone = isDteTemplateFieldVisible(
    templateSettings,
    "receiver",
    "zone"
  );

  if ((showCustomerCode && receiver.code) || (showSellerCode && receiver.sellerCode)) {
    rows.push({
      leftLabel: showCustomerCode
        ? `${getDteTemplateLabel(templateSettings, "customerCode", "Codigo cliente")} :`
        : "",
      leftValue: showCustomerCode ? receiver.code || "---" : "",
      rightLabel: showSellerCode && receiver.sellerCode
        ? `${getDteTemplateLabel(templateSettings, "sellerCode", "Codigo vendedor")}:`
        : "",
      rightValue: showSellerCode && receiver.sellerCode ? receiver.sellerCode : "",
    });
  }

  rows.push({
    leftLabel: "Razon social :",
    leftValue: receiver.name || "---",
    rightLabel: showSellerName && receiver.sellerName
      ? `${getDteTemplateLabel(templateSettings, "sellerName", "Nombre vendedor")}:`
      : "",
    rightValue: showSellerName && receiver.sellerName ? receiver.sellerName : "",
  });

  rows.push({
    leftLabel: "Direccion :",
    leftValue: [receiver.address, receiver.addressSecondary].filter(Boolean).join("\n") || "---",
    rightLabel: receiver.nit ? "NIT:" : "",
    rightValue: receiver.nit || "",
  });

  rows.push({
    leftLabel: `${receiver.documentLabel || "Documento"}:`,
    leftValue: receiver.documentNumber || "---",
    rightLabel: receiver.nrc
      ? "NRC:"
      : showEntity && receiver.entity
      ? `${getDteTemplateLabel(templateSettings, "entity", "Entidad")}:`
      : "",
    rightValue: receiver.nrc || (showEntity && receiver.entity ? receiver.entity : ""),
  });

  if (showEconomicActivity || (showEntity && receiver.entity && receiver.nrc)) {
    rows.push({
      leftLabel: showEconomicActivity
        ? `${getDteTemplateLabel(templateSettings, "economicActivity", "Actividad economica")}:`
        : "",
      leftValue: showEconomicActivity ? receiver.activity || "---" : "",
      rightLabel: showEntity && receiver.entity && receiver.nrc
        ? `${getDteTemplateLabel(templateSettings, "entity", "Entidad")}:`
        : "",
      rightValue: showEntity && receiver.entity && receiver.nrc ? receiver.entity : "",
    });
  }

  if ((showPaymentStatus && documentData.meta && documentData.meta.paymentStatus) || (showZone && receiver.zone)) {
    rows.push({
      leftLabel: showPaymentStatus
        ? `${getDteTemplateLabel(templateSettings, "paymentStatus", "Estado de la factura")}:`
        : "",
      leftValue: showPaymentStatus ? (isContadoEspecial(documentData) ? "Cancelada / Contado" : documentData.meta.paymentStatus) : "",
      rightLabel: showZone && receiver.zone
        ? `${getDteTemplateLabel(templateSettings, "zone", "Zona")}:`
        : "",
      rightValue: showZone && receiver.zone ? receiver.zone : "",
    });
  }

  if (receiver.email || receiver.phone) {
    rows.push({
      leftLabel: receiver.email ? "Correo cliente:" : "Telefono:",
      leftValue: receiver.email || receiver.phone || "---",
      rightLabel: receiver.phone && receiver.email ? "Telefono:" : "",
      rightValue: receiver.phone && receiver.email ? receiver.phone : "",
    });
  }

  if (showContact && receiver.contactName) {
    rows.push({
      leftLabel: `${getDteTemplateLabel(templateSettings, "contact", "Contacto")}:`,
      leftValue: receiver.contactName,
      rightLabel: "",
      rightValue: "",
    });
  }
  if (documentData?.meta?.invoiceType == 'DNR' && documentData?.jsonPayload?.receptor?.bienTitulo) {
    rows.push({
      leftLabel: `${getDteTemplateLabel(templateSettings, "bienTitulo", "Bienes remitidos a titulo de")}:`,
      leftValue: getBienTitulo(documentData?.jsonPayload?.receptor?.bienTitulo ?? '05'),
      rightLabel: "",
      rightValue: "",
    });
  }

  return rows;
}

function isContadoEspecial(documentData = {}) {
  const diasCredito = diferentBetweenDays(
    documentData.meta.issueDate,
    documentData.meta.dueDate
  );

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  return (
    user?.showcashinvoiceswhen1day &&
    documentData?.meta?.paymentStatus === "Crédito" &&
    diasCredito === 1
  );
}

function diferentBetweenDays(date1, date2) {
  const [d1, m1, a1] = date1.split('/').map(Number);
  const [d2, m2, a2] = date2.split('/').map(Number);

  const f1 = new Date(a1, m1 - 1, d1);
  const f2 = new Date(a2, m2 - 1, d2);

  const diferenciaMs = Math.abs(f2 - f1);
  return Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));    
}

function getBienTitulo(bienTitulo){
      return [
        { id: "01", value: "Depósito" },
        { id: "02", value: "Propiedad" },
        { id: "03", value: "Consignación" },
        { id: "04", value: "Traslado" },
        { id: "05", value: "Otros" },
      ].find(i => i.id == bienTitulo)?.value ?? 'Otros';
}
function createTotalRow(documentData, key, label, currency, value, options = {}) {
  const numericValue = asNumber(value);
  const shouldRender = options.always || numericValue > 0;

  if (!shouldRender) {
    return null;
  }

  const visibilityMap = {
    discounts: "discounts",
    exempt: "exempt",
    nonSubject: "nonSubject",
    nonTaxable: "nonTaxable",
    retention: "retention",
    perception: "perception",
    fovial: "fovial",
    cotrans: "cotrans",
  };

  const templateSettings = documentData.templateSettings || {};
  const visibilityKey = visibilityMap[key];

  if (
    visibilityKey &&
    !isDteTemplateFieldVisible(templateSettings, "totals", visibilityKey)
  ) {
    return null;
  }

  return {
    key,
    label,
    displayLabel: getDteTemplateLabel(templateSettings, key, label),
    currency: cleanText(currency, "$"),
    value: formatPlainMoney(numericValue, 2),
    emphasis: Boolean(options.emphasis),
  };
}

export function getStandardTotalRows(documentData = {}) {
  const rawData = documentData.rawData || {};
  const header = rawData.header || {};
  const invoiceType = cleanText(header.InvoiceType, "");
  const currency = cleanText(header.InvoiceCurrency, "$");
  const rows = [];

  const push = (row) => {
    if (row) {
      rows.push(row);
    }
  };

  if (invoiceType === "CDD") {
    push(createTotalRow(documentData, "total", "Total de la donacion", currency, header.InvoiceTotalPagar, {
      always: true,
      emphasis: true,
    }));
    return rows;
  }

  if (invoiceType === "FEX") {
    push(createTotalRow(documentData, "subtotal", "Total de Operaciones Afectas", currency, header.InvoiceSubTotalVentas, {
      always: true,
    }));
    push(createTotalRow(documentData, "discounts", "Monto global de Desc., Rebajas de operaciones afectas", currency, header.InvoiceTotalDescu, {
      always: true,
    }));
    push(createTotalRow(documentData, "freight", "Flete", currency, header.Flete, {
      always: true,
    }));
    push(createTotalRow(documentData, "insurance", "Seguro", currency, header.Seguro, {
      always: true,
    }));
    push(createTotalRow(documentData, "nonTaxable", "Total otros montos no afectos", currency, header.InvoiceTotalNoGravado, {
      always: true,
    }));
    push(createTotalRow(documentData, "total", "Monto Total de la Operacion", currency, header.InvoiceTotalPagar, {
      always: true,
      emphasis: true,
    }));
    return rows;
  }

  if (["DNC", "DND"].includes(invoiceType)) {
    push(createTotalRow(documentData, "subtotal", "Sub total de ventas", currency, header.InvoiceSubTotalVentas, {
      always: true,
    }));
    push(createTotalRow(documentData, "nonSubject", "Monto global Des., Rebajas y otros a ventas no sujetas", currency, header.InvoiceDescuNoSuj, {
      always: true,
    }));
    push(createTotalRow(documentData, "exempt", "Monto global Des., Rebajas y otros a ventas exentas", currency, header.InvoiceDescuExenta, {
      always: true,
    }));
    push(createTotalRow(documentData, "discounts", "Monto global Des., Rebajas y otros a ventas gravadas", currency, header.InvoiceDescuGravada, {
      always: true,
    }));
    push(createTotalRow(documentData, "iva", "Impuesto al Valor Agregado 13%", currency, header.InvoiceTotalIva, {
      always: true,
    }));
    push(createTotalRow(documentData, "retention", "IVA Retenido", currency, header.InvoiceIvaRete1, {
      always: true,
    }));
    push(createTotalRow(documentData, "total", "Total", currency, header.InvoiceTotalPagar, {
      always: true,
      emphasis: true,
    }));
    return rows;
  }

  if (invoiceType === "CCF") {
    const subtotal = (
      asNumber(header.InvoiceTotalPagar) -
      asNumber(header.InvoiceTotalNoGravado) -
      asNumber(header.InvoiceReteRenta) +
      asNumber(header.InvoiceIvaRete1) -
      asNumber(header.InvoiceIvaPerce1) -
      asNumber(header.InvoiceTotalNoSuj) -
      asNumber(header.InvoiceTotalExenta)
    );

    push(createTotalRow(documentData, "subtotal", "Sub total ventas gravadas", currency, header.InvoiceTotalGravada || header.InvoiceSubTotalVentas, {
      always: true,
    }));
    push(createTotalRow(documentData, "discounts", "Total descuentos", currency, header.InvoiceTotalDescu, {
      always: true,
    }));
    push(createTotalRow(documentData, "iva", "IVA", currency, header.InvoiceTotalIva, {
      always: true,
    }));
    push(createTotalRow(documentData, "subtotal", "Sub total", currency, subtotal, {
      always: true,
    }));
    push(createTotalRow(documentData, "retention", "IVA Retenido", currency, header.InvoiceIvaRete1, {
      always: true,
    }));
    push(createTotalRow(documentData, "perception", "IVA Percibido", currency, header.InvoiceIvaPerce1, {
      always: true,
    }));
    push(createTotalRow(documentData, "retention_isr", "Impuesto sobre la renta", currency, header.InvoiceReteRenta, {
      always: false,
    }));
    push(createTotalRow(documentData, "fovial", "Fovial", currency, header.Fovial, {
      always: true,
    }));
    push(createTotalRow(documentData, "cotrans", "Cotrans", currency, header.Cotrans, {
      always: true,
    }));
    push(createTotalRow(documentData, "exempt", "Ventas exentas", currency, header.InvoiceTotalExenta, {
      always: true,
    }));
    push(createTotalRow(documentData, "nonSubject", "Ventas no sujetas", currency, header.InvoiceTotalNoSuj, {
      always: true,
    }));
    push(createTotalRow(documentData, "nonTaxable", "Total otros montos no afectos", currency, header.InvoiceTotalNoGravado, {
      always: true,
    }));
    push(createTotalRow(documentData, "total", "Total", currency, header.InvoiceTotalPagar, {
      always: true,
      emphasis: true,
    }));
    return rows;
  }

  push(createTotalRow(documentData, "subtotal", "Sub total de ventas", currency, header.InvoiceSubTotalVentas || header.InvoiceTotalGravada, {
    always: true,
  }));
  push(createTotalRow(documentData, "discounts", "Total descuentos", currency, header.InvoiceTotalDescu, {
    always: true,
  }));
  if (invoiceType !== "FNS") {
    push(createTotalRow(documentData, "retention", "IVA Retenido", currency, header.InvoiceIvaRete1, {
      always: true,
    }));
  }
  const relatedDocuments = [].concat(
    rawData.docRelated || [],
    header.docRelated || [],
    rawData.ndornc || header.ndornc || [],
    header.JsonMh?.dteJson?.documentoRelacionado || [],
    header.jsonMH?.documentoRelacionado || [],
    rawData.dteJson?.documentoRelacionado || []
  );
  const isFerFromFns = invoiceType === "FER" && relatedDocuments.some((document) =>
    ["14", "FNS"].includes(String(document?.TipoDocumentoRelacionado || document?.tipoDocumento))
  );

  if (invoiceType === "FNS" || isFerFromFns) {
    push(createTotalRow(documentData, "retention_isr", "Impuesto sobre la renta", currency, header.InvoiceReteRenta, {
      always: true,
    }));
  }
  push(createTotalRow(documentData, "fovial", "Fovial", currency, header.Fovial, {
    always: true,
  }));
  push(createTotalRow(documentData, "cotrans", "Cotrans", currency, header.Cotrans, {
    always: true,
  }));
  push(createTotalRow(documentData, "exempt", "Ventas exentas", currency, header.InvoiceTotalExenta, {
    always: true,
  }));
  push(createTotalRow(documentData, "nonSubject", "Ventas no sujetas", currency, header.InvoiceTotalNoSuj, {
    always: true,
  }));
  push(createTotalRow(documentData, "nonTaxable", "Total otros montos no afectos", currency, header.InvoiceTotalNoGravado, {
    always: true,
  }));
  push(createTotalRow(documentData, "total", "Total", currency, header.InvoiceTotalPagar, {
    always: true,
    emphasis: true,
  }));

  return rows;
}
